import { useState, useEffect } from 'react';
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/solid';

interface PaginationProps {
  pageNumber: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  pageNumber,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const [pageInput, setPageInput] = useState(String(pageNumber));

  useEffect(() => {
    setPageInput(String(pageNumber));
  }, [pageNumber]);

  const isFirst = pageNumber === 1 || totalPages <= 1;
  const isLast = pageNumber >= totalPages || totalPages <= 1;
  const clamp = (n: number) => Math.max(1, Math.min(n, totalPages));

  const commit = () => {
    const n = parseInt(pageInput, 10);
    const c = isNaN(n) ? pageNumber : clamp(n);
    if (c !== pageNumber) onPageChange(c);
    else setPageInput(String(pageNumber));
  };

  const btnBase =
    'flex items-center justify-center rounded p-1.5 transition-colors border';
  const btnActive =
    'border-[var(--dash-border-strong)] bg-[var(--dash-card-light)] text-[var(--dash-text-muted)] hover:border-[var(--dash-border-strong)] hover:text-[var(--dash-text)] cursor-pointer';
  const btnDisabled =
    'border-[var(--dash-border)] bg-[var(--dash-bg)] text-[var(--dash-text-faint)] cursor-not-allowed';

  return (
    <div className="mt-6 flex items-center justify-center gap-2">
      <button
        className={`${btnBase} ${isFirst ? btnDisabled : btnActive}`}
        onClick={() => onPageChange(1)}
        disabled={isFirst}
        aria-label="First page"
      >
        <ChevronDoubleLeftIcon className="h-4 w-4" />
      </button>
      <button
        className={`${btnBase} ${isFirst ? btnDisabled : btnActive}`}
        onClick={() => onPageChange(pageNumber - 1)}
        disabled={isFirst}
        aria-label="Previous page"
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-2 text-sm text-[var(--dash-text-faint)]">
        <span>Page</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
          onBlur={commit}
          disabled={totalPages <= 1}
          className="w-14 rounded border border-[var(--dash-border-strong)] bg-[var(--dash-bg)] px-2 py-1 text-center text-sm text-[var(--dash-text)] focus:border-[var(--dash-border-strong)] focus:outline-none disabled:text-[var(--dash-text-faint)]"
          aria-label="Page number"
        />
        <span>of {totalPages}</span>
      </div>

      <button
        className={`${btnBase} ${isLast ? btnDisabled : btnActive}`}
        onClick={() => onPageChange(pageNumber + 1)}
        disabled={isLast}
        aria-label="Next page"
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
      <button
        className={`${btnBase} ${isLast ? btnDisabled : btnActive}`}
        onClick={() => onPageChange(totalPages)}
        disabled={isLast}
        aria-label="Last page"
      >
        <ChevronDoubleRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
